import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import qs from "query-string";
import { useLocation } from "react-router-dom";

import { usePrepareData } from "utils/usePrepareData";

function fitMapbounds(mapsApi, points, map) {
  const bounds = new mapsApi.LatLngBounds();
  points.forEach(point => {
    if (point.coordinates) {
      point.lat = point.coordinates.latitude;
      point.lng = point.coordinates.longitude;
    }
    bounds.extend({ lat: parseFloat(point.lat), lng: parseFloat(point.lng) });
  });
  map.fitBounds(bounds);
}

function setMapCenter(map, lat, lng) {
  map.setCenter({
    lat: lat,
    lng: lng,
  });
}

function isNumber(val) {
  return !isNaN(val);
}

const KARLSRUNE_COODINATES = {
  latitude: 49.009732,
  longitude: 8.4006339,
};

const DEFAULT_COORDINATES = {
  center: {
    lat: KARLSRUNE_COODINATES.latitude,
    lng: KARLSRUNE_COODINATES.longitude,
  },
  zoom: 13,
};

function normalizeUserPosition(userPosition) {
  const lat = userPosition.latitude;
  const lon = userPosition.longitude;
  return {
    start_lat: lat,
    start_lon: lon,
    end_lat: lat,
    end_lon: lon,
  };
}
function getWhereToGo(userPosition, savedLocation) {
  if (userPosition) {
    return normalizeUserPosition(userPosition);
  }
  if (savedLocation) {
    return savedLocation;
  }
  return normalizeUserPosition(KARLSRUNE_COODINATES);
}

export default function useMapsCoordinatesManager(selectedCompany) {
  const map = useRef(null);
  const [mapsApi, setMapsApi] = useState(null);

  const { userPosition, savedLocation, savedLocationLoading, userPosLoading } = usePrepareData();

  const location = useLocation();
  const [urlCoordinates] = useState(qs.parse(location.search));
  const coodinates = useRef(undefined);
  const goToPoint = useCallback(p => {
    setMapCenter(
      map.current.map_,
      (parseFloat(p.start_lat) + parseFloat(p.end_lat)) / 2,
      (parseFloat(p.start_lon) + parseFloat(p.end_lon)) / 2
    );
  }, []);

  const setToUserPosition = useCallback(() => {
    goToPoint(getWhereToGo(userPosition, savedLocation));
  }, [goToPoint, savedLocation, userPosition]);

  const initialCoords = useMemo(() => {
    if (!isNumber(urlCoordinates.start_lat) && !userPosition && !savedLocation) {
      return null;
    }
    if (isNumber(urlCoordinates.start_lat)) {
      return urlCoordinates;
    }
    if (savedLocation) {
      return savedLocation;
    }

    if (userPosition && !savedLocationLoading) {
      return normalizeUserPosition(userPosition);
    }
  }, [urlCoordinates, userPosition, savedLocation, savedLocationLoading]);

  useEffect(() => {
    if (coodinates.current === undefined && initialCoords) {
      if (!map.current || !map.current.map_) {
        return;
      }
      coodinates.current = true;
      goToPoint(initialCoords);
    }
  }, [goToPoint, initialCoords, savedLocationLoading, userPosLoading, userPosition]);

  const handleApiLoaded = useCallback(
    (_map, maps) => {
      setMapsApi(maps);
    },
    [setMapsApi]
  );

  const fitMapPoints = useCallback(
    points => {
      if (mapsApi) {
        fitMapbounds(mapsApi, points, map.current.map_);
      }
    },
    [mapsApi]
  );

  useEffect(() => {
    if (!map.current || !map.current.map_) {
      return;
    }

    if (selectedCompany) {
      const { lat, lon } = selectedCompany;
      if (lat && lon) {
        setMapCenter(map.current.map_, lat, lon);
      }
    }
  }, [selectedCompany]);

  return [setToUserPosition, DEFAULT_COORDINATES, handleApiLoaded, fitMapPoints, map, userPosition];
}
